import { PageHeader, CtySpinner, ErrorBoundary, CustomTabs, AuditLogsAgGridWrapper, FGA_AUDIT_LOG_STATIC_FILTERS } from "@circuitry-ai/doc-ui";
import { useGetFgaGroupsQuery } from "../../../shared/data/src/features/settings/settingsApiSlice";
import { Tabs, Tab } from '@nextui-org/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { GroupsTab } from './tabs/GroupsTab';
import { ResourcesTab } from './tabs/ResourcesTab';
import { UsersTab } from './tabs/UsersTab';

// Hoist ErrorBoundary-wrapped components to module scope
const PageHeaderWithErrorBoundary = ErrorBoundary(PageHeader);

export function FgaSettings() {
  const { t } = useTranslation();
  const [selectedPageTab, setSelectedPageTab] = useState<string>('access-management');
  const [selectedTab, setSelectedTab] = useState<string>('groups');
  
  // API hooks
  const { isLoading: groupsLoading, error: groupsError} = useGetFgaGroupsQuery();

  if (groupsLoading) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeaderWithErrorBoundary heading="Access Management" description="Manage roles, resources, and user access through role-based permissions" pageHeaderClass='pb-2 !px-0' />
        <div className="bg-content1 rounded-lg border p-6">
          <div className="flex items-center justify-center py-8">
            <CtySpinner size="md" />
            <span className="ml-2 text-default-500">Loading Access Management...</span>
          </div>
        </div>
      </div>
    );
  }
  if (groupsError) {
    return (
      <div className="flex flex-col gap-4 h-full">
        <PageHeaderWithErrorBoundary heading="Access Management" description="Manage roles, resources, and user access through role-based permissions" pageHeaderClass='pb-2 !px-0' />
        <div className="bg-content1 rounded-lg border flex-1 flex items-center justify-center">
          <div className="text-xl text-danger">
            Access Denied
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeaderWithErrorBoundary heading="Access Management" description="Manage roles, resources, and user access through role-based permissions" pageHeaderClass='pb-2 !px-0' />

      <CustomTabs
        tabs={[
          { key: 'access-management', title: t('Access Management'), id: 'fga-access-management-tab' },
          { key: 'audit-logs', title: t('Audit Logs'), id: 'fga-audit-logs-tab' },
        ]}
        selectedKey={selectedPageTab}
        onTabChange={setSelectedPageTab}
      />

      {selectedPageTab === 'access-management' && (
        <div className="bg-content1 rounded-lg border p-6">
          <Tabs 
            aria-label="FGA Configuration" 
            className="w-full" 
            variant="underlined" 
            selectedKey={selectedTab}
            onSelectionChange={(key) => setSelectedTab(key as string)}
            classNames={{
              tabList: 'gap-6 w-full relative p-0 border-b border-divider', cursor: 'w-full bg-primary', tab: 'px-0 h-12', tabContent: 'group-data-[selected=true]:text-primary',
            }}>
            <Tab key="groups" title="Roles" className="p-0">
              {selectedTab === 'groups' && <GroupsTab />}
            </Tab>
            <Tab key="resources" title="Resources" className="p-0">
              {selectedTab === 'resources' && <ResourcesTab />}
            </Tab>
            <Tab key="users" title="Users" className="p-0">
              {selectedTab === 'users' && <UsersTab />}
            </Tab>
          </Tabs>
        </div>
      )}

      {selectedPageTab === 'audit-logs' && (
        <div className="pt-2">
          <AuditLogsAgGridWrapper staticFilters={FGA_AUDIT_LOG_STATIC_FILTERS} />
        </div>
      )}
    </div>
  );
}

export default FgaSettings; 